import React, {useState, useEffect, SetStateAction, Dispatch} from 'react';
import StopTimerDialog from '../StopTimerDialog/StopTimerDialog';
import {
  getCCMPatientDetails,
  startTimerCCM
} from '@shared/services/CCMServices';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {useSnackbar} from 'notistack';
import {Trans, useTranslation} from 'react-i18next';

import {
  _ccmResetTimer,
  _ccmStartTimer,
  _ccmStopTimer,
  _startTimerfromPatientDrawer
} from '@shared/store/actions/CCMTimerActions';
import {useDispatch, useSelector} from 'react-redux';
import {Button, makeStyles} from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import Storage from 'src/utils/Storage';
import WarningTimerDialog from '../StopTimerDialog/WarningTimerDialog';
import ConfirmationDialog from '@components/system/ConfirmationDialog/ConfirmationDialog';
import {APP, DEFAULT_RED_COLOR} from 'src/style/variable';
import {AppPermissions, CCMConstants} from '@shared/constants/Mapping';
import {CommunicationService} from 'src/service/CommunicationService';
import {logoutUserFromWeb} from '@shared/services/CommonServices';
import NavRoutes from 'src/constants/route/NavRoutes';
import {useHistory} from 'react-router-dom';
import WithPermission from '@components/system/WithPermission/WithPermission';
import RunningTime from './RunningTime';
interface TimerProps {
  groupId: number;
  clinicId?: number;
  patientId?: number;
  ccmPatientId: number;
  guarantorId?: number;
  isPlanActive?: boolean;
  isExternalPlanActive?: boolean;
  patientName?: string;
  patientAccountNumber?: string | number;
  guarantorNotificationPhone?: string | number;

  initialStartTimer?: boolean;
  initialStartTimerValue?: any;
  handleParentTimeStart?: () => void;
  setShowLogoutStopTimerDialog?: Dispatch<SetStateAction<boolean>>;
  isTimerActive?: boolean;
  isFromTopAppBar?: boolean;
  startTimerLabel?: string;
  stopTimerLabel?: string;
  logoutStopTime?: boolean;
  handleInitialStartTimer?: () => void;
  showStopTimerDialog?: boolean;
  setShowRecordDetailsCCMpatientsTable?: (data: any) => void;
  handleCancelCCMStopTimer?: () => void;
  isFromFloatingPanel?: boolean;
  isFromPatientDrawer?: boolean;
}

const useStyles = makeStyles(() => ({
  runningTimeDiv: {
    marginLeft: 5,
    fontSize: '1rem',
    fontWeight: 700
  },
  stopTimerButton: {
    padding: '0.2rem 0.5rem',
    backgroundColor: DEFAULT_RED_COLOR,
    color: APP.COLOR.DEFAULT.WHITE,
    '&:hover': {
      backgroundColor: DEFAULT_RED_COLOR,
      color: APP.COLOR.DEFAULT.WHITE
    }
  },
  accessTimeIcon: {
    fontSize: '19px',
    marginBottom: '2px',
    display: 'flex',
    alignItems: 'center'
  },
  stopTimeWrapperDefault: {
    display: 'flex',
    alignItems: 'center'
  },
  stopTimeWrapperTopAppBar: {
    backgroundColor: APP.COLOR.DEFAULT.WHITE,
    color: APP.COLOR.PRIMARY,
    padding: '2px 4px',
    borderRadius: '4px'
  },
  stopTimerButtonWrapper: {
    marginLeft: '8px'
  },
  stopTimerButtonLabel: {
    marginLeft: '-5px',
    whiteSpace: 'nowrap'
  },
  startTimerButton: {
    padding: '0.2rem 0.5rem',
    backgroundColor: APP.COLOR.PRIMARY,
    color: APP.COLOR.DEFAULT.WHITE
  },
  startTimerButtonTopAppBar: {
    backgroundColor: APP.COLOR.DEFAULT.WHITE,
    color: APP.COLOR.PRIMARY
  },
  floatingTimerWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 5,
    backgroundColor: '#dfecf4',
    width: 200,
    height: 200,
    borderRadius: '50%',
    justifyContent: 'center',
    color: APP.COLOR.PRIMARY,
    fontWeight: 'bold',
    fontSize: '2.5rem',
    position: 'relative',
    boxShadow:
      '-12px -12px 24px rgba(108, 141, 194, 0.1), 12px 10px 24px rgba(108, 141, 194, 0.25)'
  },
  stopTimeFloatingTimer: {
    color: APP.COLOR.DEFAULT.WHITE,
    position: 'absolute',
    bottom: '-24',
    right: '-24',
    width: 88,
    height: 88,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    fontSize: '1rem',
    backgroundColor: DEFAULT_RED_COLOR,
    border: 0,
    whiteSpace: 'nowrap',
    boxShadow:
      '-12px -12px 24px rgba(194, 108, 108, 0.1), 12px 10px 24px rgba(194, 108, 108, 0.25)'
  },
  startTimerLabel: {
    marginLeft: '-5px',
    whiteSpace: 'nowrap',
    lineHeight: 1
  },
  stopTimerLabel: {
    fontWeight: 'bold'
  }
}));

const Timer: React.FC<TimerProps> = ({
  groupId,
  clinicId,
  patientId,
  ccmPatientId,
  guarantorId,
  isPlanActive,
  isExternalPlanActive,
  patientName,
  patientAccountNumber,
  guarantorNotificationPhone,
  logoutStopTime,
  handleParentTimeStart,
  isFromTopAppBar = false,
  setShowLogoutStopTimerDialog,
  initialStartTimer = false,
  startTimerLabel,
  stopTimerLabel,
  setShowRecordDetailsCCMpatientsTable,
  handleInitialStartTimer,
  showStopTimerDialog: initialShowStopTimerDialog = false,
  handleCancelCCMStopTimer,
  isFromFloatingPanel = false,
  isFromPatientDrawer = false
}) => {
  const [showDialog, setShowDialog] = useState({
    showStopTimerDialog: false,
    showWarningDialog: false,
    showConfirmationDialogToStartTimer: false
  });
  const {
    showStopTimerDialog,
    showWarningDialog,
    showConfirmationDialogToStartTimer
  } = showDialog;
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const timerInfo = useSelector((state: any) => state.ccmTimer);
  const {isTimerRunning: ccmStartTimeRunning, startTime} = timerInfo;
  let localStorageData: any = Storage.getItem(
    CCMConstants.STORAGE_CCM_TIMER_DATA
  );
  localStorageData = JSON?.parse(localStorageData || null);
  const {
    isSinglePatientSearch,
    patientName: isSinglePatientName,
    startTimerfromPatientDrawer
  } = useSelector((state: any) => state.ccmTimer);

  const patientDetails = useSelector(
    (state: any) => state.patientDetailsDrawer.patientDetails
  );

  // Get the data stored in local storage, and set it to the local state
  useEffect(() => {
    const storedTimerData = Storage.getItem(
      CCMConstants.STORAGE_CCM_TIMER_DATA
    );
    if (storedTimerData) {
      const parsedTimerData = JSON.parse(storedTimerData);
      if (parsedTimerData.isRunning) {
        dispatch(
          _ccmStartTimer(
            parsedTimerData.startTime,
            parsedTimerData.ccmPatientId
          )
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialShowStopTimerDialog) {
      dispatch(_ccmStopTimer(Date.now()));
      setShowDialog(prevState => ({
        ...prevState,
        showStopTimerDialog: true
      }));
    }
  }, [initialShowStopTimerDialog]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateCCMPatientDetailsData = async () => {
    try {
      const res = await getCCMPatientDetails(groupId, ccmPatientId, {
        clinic_id: clinicId ?? timerInfo.clinicId
      });
      if (res?.data) {
        let timerData: any = Storage.getItem(
          CCMConstants.STORAGE_CCM_TIMER_DATA
        );
        timerData = JSON.parse(timerData || null);
        Storage.setItem(
          CCMConstants.STORAGE_CCM_TIMER_DATA,
          JSON.stringify({
            ...timerData,
            ccmPanelData: {
              ...timerData?.ccmPanelData,
              prevFollowUpNotes: res.data?.follow_up_notes,
              prevFollowUpNotesLogId: res.data?.follow_up_note_log_id
            }
          })
        );
        CommunicationService.sendEvent({
          action: 'CCM_PANEL_DATA_UPDATED',
          data: null
        });
      }
    } catch (error: any) {
      enqueueSnackbar(error.message, SnackBarConfig('e'));
    }
  };

  const updateSelectedPatientTimerData = (res: any, currentDate: Date) => {
    const currentTime = currentDate.getTime();
    const ccmPanelData = {
      groupId: groupId,
      clinicId: clinicId ?? timerInfo.clinicId,
      patientId: patientId ?? timerInfo.patientId,
      ccmPatientId: ccmPatientId ?? timerInfo.ccmPatientId,
      guarantorId: guarantorId ?? timerInfo.guarantorId,
      isPlanActive: isPlanActive ?? timerInfo.isPlanActive,
      isExternalPlanActive:
        isExternalPlanActive ?? timerInfo.isExternalPlanActive,
      patientName: patientName ?? timerInfo.patientName,
      patientAccountNumber: patientAccountNumber ?? timerInfo.accountNumber,
      guarantorNotificationPhone:
        guarantorNotificationPhone ?? timerInfo.guarantorNotificationPhone
    };
    Storage.setItem(
      CCMConstants.STORAGE_CCM_TIMER_DATA,
      JSON.stringify({
        startTime: currentTime,
        isRunning: true,
        patientId: patientId,
        ccmPatientId: ccmPatientId,
        groupId: groupId,
        patientName: patientName,
        patientAccountNumber: patientAccountNumber,
        ccmPatientTimerLogId: res?.data?.ccm_patient_log_id,
        clinicId: clinicId ?? timerInfo.clinicId,
        ccmPanelData
      })
    );
    dispatch(_ccmStartTimer(currentTime, ccmPatientId));
  };

  useEffect(() => {
    const subscription = CommunicationService.getEvent().subscribe(event => {
      if (
        event.action === 'CCM_PLAN_CREATED' ||
        event.action === 'CCM_PLAN_DELETED'
      ) {
        let timerData: any = Storage.getItem(
          CCMConstants.STORAGE_CCM_TIMER_DATA
        );
        timerData = JSON.parse(timerData || null);
        if (
          timerData &&
          timerData?.ccmPanelData &&
          event.data?.ccmPatientId == timerData?.ccmPanelData?.ccmPatientId
        ) {
          Storage.setItem(
            CCMConstants.STORAGE_CCM_TIMER_DATA,
            JSON.stringify({
              ...timerData,
              ccmPanelData: {
                ...timerData?.ccmPanelData,
                isPlanActive: event.action === 'CCM_PLAN_CREATED' ? true : false
              }
            })
          );
          CommunicationService.sendEvent({
            action: 'CCM_PANEL_DATA_UPDATED',
            data: null
          });
        }
      } else if (event.action === 'CCM_PLAN_TASK_DETAILS_UPDATED') {
        let timerData: any = Storage.getItem(
          CCMConstants.STORAGE_CCM_TIMER_DATA
        );
        timerData = JSON.parse(timerData || null);
        if (
          timerData &&
          timerData?.ccmPanelData &&
          event.data?.ccmPatientId == timerData?.ccmPanelData?.ccmPatientId
        ) {
          Storage.setItem(
            CCMConstants.STORAGE_CCM_TIMER_DATA,
            JSON.stringify({
              ...timerData,
              ccmPanelData: {
                ...timerData?.ccmPanelData,
                isExternalPlanActive: false
              }
            })
          );
          CommunicationService.sendEvent({
            action: 'CCM_PANEL_DATA_UPDATED',
            data: null
          });
        }
      }
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const init = async () => {
    try {
      // If timer is starting from ccmpatientsDrawerDetails - then the init method should call only once
      // That is the reason we are making _startTimerfromPatientDrawer making as false here.
      // dispatch(_startTimerfromPatientDrawer(false));
      const date = new Date();
      const utcDate = date.toUTCString();
      const payload: any = {
        start_time: utcDate
      };
      const options: Record<string, any> = {};
      if (clinicId ?? timerInfo.clinicId) {
        options.clinic_id = clinicId ?? timerInfo.clinicId;
      }
      if (!ccmPatientId) return;
      const res = await startTimerCCM(groupId, ccmPatientId, payload, options);
      if (res) {
        updateSelectedPatientTimerData(res, date);

        // The below call is just to update the follow up notes
        updateCCMPatientDetailsData();

        enqueueSnackbar(
          t('CCM.START_TIMER.TIMER_STARTED'),
          SnackBarConfig('s')
        );
        handleParentTimeStart && handleParentTimeStart();
        if (setShowRecordDetailsCCMpatientsTable)
          setShowRecordDetailsCCMpatientsTable(true);
      }
    } catch (error: any) {
      enqueueSnackbar(error.message, SnackBarConfig('e'));
      // If timer is starting from ccmpatientsDrawerDetails - then the init method should call only once
      // If there is an error in starting the timer, then make _startTimerfromPatientDrawer as true, then we can start the timer again
      dispatch(_startTimerfromPatientDrawer(true));
    }
  };

  const showWarningTimerDialogComp = () => {
    setShowDialog(prevState => ({
      ...prevState,
      showWarningDialog: true
    }));
  };

  const showStopTimerDialogComp = () => {
    setShowDialog(prevState => ({
      ...prevState,
      showStopTimerDialog: true
    }));
  };

  const closeStopTimerDialog = () => {
    setShowDialog(prevState => ({
      ...prevState,
      showStopTimerDialog: false
    }));
    if (handleInitialStartTimer) handleInitialStartTimer();
  };

  const closeWarningTimerDialog = () => {
    setShowDialog(prevState => ({
      ...prevState,
      showWarningDialog: false
    }));
    if (handleInitialStartTimer) handleInitialStartTimer();
  };
  useEffect(() => {
    closeStopTimerDialog();
    closeWarningTimerDialog();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStopTimer = () => {
    const currentTime = Date.now();
    closeWarningTimerDialog();
    dispatch(_ccmStopTimer(currentTime));
    showStopTimerDialogComp();
  };

  const handleCancelDialog = () => {
    setShowDialog(prevState => ({
      ...prevState,
      showStopTimerDialog: false
    }));
    if (setShowLogoutStopTimerDialog) setShowLogoutStopTimerDialog(false);
  };

  const handleTimerStart = (timerIsNotRunning?: boolean) => {
    if (!ccmStartTimeRunning || !!timerIsNotRunning) {
      init();
    } else {
      showWarningTimerDialogComp();
    }
  };

  useEffect(() => {
    if (
      !ccmStartTimeRunning &&
      startTimerfromPatientDrawer &&
      ccmPatientId === patientDetails?.ccm_patient_id
    ) {
      handleTimerStart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTimerfromPatientDrawer]);

  useEffect(() => {
    const subscription = CommunicationService.getEvent().subscribe(event => {
      if (
        event.action === 'START_TIMER_FROM_PATIENT_DRAWER_REMINDER' &&
        isFromPatientDrawer &&
        ccmPatientId === patientDetails?.ccm_patient_id
      ) {
        handleTimerStart();
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccmStartTimeRunning, ccmPatientId, patientDetails?.ccm_patient_id]);

  const logoutFromApp = async () => {
    try {
      await logoutUserFromWeb();
      history.push(`/${NavRoutes.PUBLIC.LOGOUT}`);
    } catch (error) {
      history.push(`/${NavRoutes.PUBLIC.LOGOUT}`);
    }
  };

  const handleSaveDialog = () => {
    setShowDialog(prevState => ({
      ...prevState,
      showStopTimerDialog: false
    }));

    dispatch(_ccmResetTimer());
    if (ccmPatientId != timerInfo.ccmPatientId) handleTimerStart(true);

    if (handleCancelCCMStopTimer) handleCancelCCMStopTimer();
    logoutStopTime && logoutFromApp();
  };

  useEffect(() => {
    if (initialStartTimer) {
      handleTimerStart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStartTimer]);

  useEffect(() => {
    if (logoutStopTime) {
      handleStopTimer();
    }
  }, [logoutStopTime]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartTimer = () => {
    if (isFromTopAppBar) {
      if (ccmPatientId && isSinglePatientSearch) {
        // make usestate true for confirmation dialog
        setShowDialog(prevState => ({
          ...prevState,
          showConfirmationDialogToStartTimer: true
        }));
      } else {
        handleParentTimeStart && handleParentTimeStart();
      }
    } else {
      // don't call API when timer is already running
      handleTimerStart();
    }
  };

  const getTimerStatus = () => {
    let runningTimer = false;

    let timerGroupId: number | undefined;
    let timerCCMPatientId: number | undefined;
    const storedTimerData = Storage.getItem(
      CCMConstants.STORAGE_CCM_TIMER_DATA
    );
    if (storedTimerData) {
      const parsedTimerData = JSON.parse(storedTimerData);
      timerGroupId = parsedTimerData?.groupId;
      timerCCMPatientId = parsedTimerData?.ccmPatientId;
    }

    if (timerGroupId != groupId) {
      return runningTimer;
    }

    if (isFromTopAppBar) {
      runningTimer = !!ccmStartTimeRunning;
    } else {
      if (ccmStartTimeRunning) {
        if (ccmPatientId && ccmPatientId === timerCCMPatientId) {
          runningTimer = true;
        }
      }
    }
    return runningTimer;
  };

  const renderDefaultTimer = () => {
    return (
      <div>
        <div>
          {getTimerStatus() ? (
            <div
              className={classes.stopTimeWrapperDefault}
              style={{
                backgroundColor: isFromTopAppBar ? APP.COLOR.DEFAULT.WHITE : '',
                color: isFromTopAppBar ? APP.COLOR.PRIMARY : '',
                padding: isFromTopAppBar ? '2px 4px' : '',
                borderRadius: isFromTopAppBar ? '4px' : ''
              }}>
              <div className={classes.runningTimeDiv}>
                <RunningTime startTime={new Date(startTime)} />
              </div>
              <div className={classes.stopTimerButtonWrapper}>
                <Button
                  startIcon={
                    <AccessTimeIcon className={classes.accessTimeIcon} />
                  }
                  className={classes.stopTimerButton}
                  variant="outlined"
                  onClick={() => {
                    handleStopTimer();
                  }}>
                  <span className={classes.stopTimerButtonLabel}>
                    {stopTimerLabel
                      ? stopTimerLabel
                      : t('CCM.STOP_TIMER.STOP_TIMER')}
                  </span>
                </Button>
              </div>
            </div>
          ) : (
            <Button
              startIcon={<AccessTimeIcon className={classes.accessTimeIcon} />}
              className={`${classes.startTimerButton}`}
              style={{
                backgroundColor: isFromTopAppBar
                  ? APP.COLOR.DEFAULT.WHITE
                  : APP.COLOR.PRIMARY,
                color: isFromTopAppBar
                  ? APP.COLOR.PRIMARY
                  : APP.COLOR.DEFAULT.WHITE
              }}
              variant="outlined"
              onClick={() => {
                handleStartTimer();
              }}>
              <div>
                <span className={classes.startTimerLabel}>
                  {startTimerLabel
                    ? startTimerLabel
                    : t('CCM.START_TIMER.START_TIMER')}
                </span>
              </div>
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderFloatingPanelTimer = () => {
    return (
      <div className={classes.floatingTimerWrapper}>
        <RunningTime startTime={new Date(startTime)} />
        <Button
          className={classes.stopTimeFloatingTimer}
          variant="outlined"
          style={{
            color: APP.COLOR.DEFAULT.WHITE,
            backgroundColor: DEFAULT_RED_COLOR
          }}
          onClick={() => {
            handleStopTimer();
          }}>
          <span className={classes.stopTimerLabel}>
            {t('CCM.STOP_TIMER.STOP_TIMER')}
          </span>
        </Button>
      </div>
    );
  };
  return (
    <>
      {isFromFloatingPanel ? renderFloatingPanelTimer() : renderDefaultTimer()}
      {showStopTimerDialog && (
        <StopTimerDialog
          open={showStopTimerDialog}
          onClose={closeStopTimerDialog}
          onCancel={handleCancelDialog}
          onSave={handleSaveDialog}
        />
      )}
      {showWarningDialog && (
        <WarningTimerDialog
          open={showWarningDialog}
          onClose={closeWarningTimerDialog}
          onSave={handleStopTimer}
          titleHeader={t('CCM.WARNING.DIALOG_HEADER')}
          contentBody={
            <Trans
              i18nKey={'CCM.WARNING.DIALOG_CONTENT'}
              values={{
                patient_name: localStorageData.patientName,
                new_patient_name: patientName
              }}
              components={[<strong key={'X'} />]}
            />
          }
          cancelBtnText={t('COMMON.NO')}
          confirmBtnText={t('COMMON.YES')}
        />
      )}
      {showConfirmationDialogToStartTimer && (
        <ConfirmationDialog
          open={showConfirmationDialogToStartTimer}
          title={t('CCM.CCM_START_TIMER_NOW_TITLE')}
          description={
            <Trans
              i18nKey={t('CCM.CCM_START_TIMER_NOW_SUBTITLE', {
                name: `(${isSinglePatientName})`
              })}
              components={[<strong key={'X'} />]}
            />
          }
          confirmButtonText={t('INSTALLMENTS.YES')}
          cancelButtonText={t('INSTALLMENTS.NO')}
          onClose={response => {
            if (response === 'confirm') {
              handleTimerStart();
              setShowDialog(prevState => ({
                ...prevState,
                showConfirmationDialogToStartTimer: false
              }));
            } else {
              setShowDialog(prevState => ({
                ...prevState,
                showConfirmationDialogToStartTimer: false
              }));
            }
          }}
        />
      )}
    </>
  );
};

export default WithPermission(
  React.memo(Timer),
  AppPermissions.CCM.NAME,
  AppPermissions.CCM.LIST
);
